import React, { useState } from 'react';
import { TrendingUp, Calendar, BarChart3, ExternalLink, X, ChevronUp, ChevronDown, Minus, ChevronRight, Brain } from 'lucide-react';
import { useAppearance } from '../contexts/AppearanceContext';
import { executeRankCommand as callRankAPI } from '../services/slashCommandsApi';

const RankBotProgress = ({ status, message }) => {
  const { isSuppleMode } = useAppearance();
  
  return (
    <div className={`mb-4 p-3 rounded-lg ${
      isSuppleMode 
        ? 'supple-card-glass supple-border-gradient-blue' 
        : 'bg-blue-50 border border-blue-200'
    }`}>
      <div className="flex items-center gap-2">
        <div className={`w-4 h-4 rounded-full ${
          status === 'loading' ? 'animate-pulse bg-blue-500' : 
          status === 'success' ? 'bg-green-500' : 'bg-red-500'
        }`} />
        <span className={`text-sm ${
          isSuppleMode ? 'supple-text-primary' : 'text-gray-700'
        }`}>{message}</span>
      </div>
    </div>
  );
};

const RankPreviewCard = ({ projectData, onViewRankings, onAnalyseRankings }) => {
  const { isSuppleMode } = useAppearance();
  
  return (
    <div className={`mt-2 p-3 rounded-lg w-full max-w-sm ${
      isSuppleMode 
        ? 'supple-card-glass supple-border-gradient-blue supple-shadow-md backdrop-blur-md' 
        : 'border border-blue-200 bg-gradient-to-r from-blue-50 to-green-50'
    }`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isSuppleMode 
            ? 'supple-btn-gradient-blue supple-text-white' 
            : 'bg-gradient-to-r from-blue-500 to-green-500 text-white'
        }`}>
          <TrendingUp className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className={`font-medium truncate ${
              isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
            }`}>Rank Report</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              isSuppleMode 
                ? 'supple-badge-blue'
                : 'bg-blue-100 text-blue-700'
            }`}>
              {projectData.projectName}
            </span>
          </div>
          <p className={`text-sm mb-1 ${
            isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
          }`}>Project ID: {projectData.projectId}</p>
          <div className="flex gap-2">
            <button
              onClick={onViewRankings}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg transition-colors ${
                isSuppleMode 
                  ? 'supple-btn-gradient-blue hover:supple-btn-gradient-blue-hover supple-text-white' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <BarChart3 className="w-3 h-3" />
              View Rankings
            </button>
            <button
              onClick={onAnalyseRankings}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg transition-colors ${
                isSuppleMode 
                  ? 'supple-btn-gradient-purple hover:supple-btn-gradient-purple-hover supple-text-white' 
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              <Brain className="w-3 h-3" />
              Analyse Rankings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RankModal = ({ projectData, positionData, onClose }) => {
  const { isSuppleMode } = useAppearance();
  
  if (!positionData || !positionData.length) return null;

  // Calculate summary statistics
  const calculateSummary = () => {
    const totalKeywords = positionData.length;
    let page1 = 0, page2 = 0, page3 = 0, notFound = 0;
    
    positionData.forEach(keyword => {
      const latestPosition = keyword.positions[keyword.positions.length - 1];
      if (latestPosition.pos === 0) {
        notFound++;
      } else if (latestPosition.pos <= 10) {
        page1++;
      } else if (latestPosition.pos <= 20) {
        page2++;
      } else if (latestPosition.pos <= 30) {
        page3++;
      }
    });
    
    return { totalKeywords, page1, page2, page3, notFound };
  };

  const summary = calculateSummary();

  const getPositionChange = (positions) => {
    if (positions.length < 2) return null;
    const current = positions[positions.length - 1];
    const previous = positions[positions.length - 2];
    return current.pos - previous.pos;
  };

  const getChangeIcon = (change) => {
    if (change === null || change === 0) return <Minus className="w-4 h-4 text-gray-500" />;
    if (change > 0) return <ChevronDown className="w-4 h-4 text-red-500" />;
    return <ChevronUp className="w-4 h-4 text-green-500" />;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className={`w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-lg ${
        isSuppleMode 
          ? 'supple-card-glass supple-border-gradient-blue backdrop-blur-md' 
          : 'bg-white border border-gray-200'
      }`}>
        {/* Header */}
        <div className={`p-6 border-b ${
          isSuppleMode ? 'supple-border-subtle' : 'border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isSuppleMode 
                  ? 'supple-btn-gradient-blue' 
                  : 'bg-blue-500'
              }`}>
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className={`text-xl font-semibold ${
                  isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                }`}>Ranking Positions</h2>
                <p className={`text-sm ${
                  isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
                }`}>{projectData.projectName} (ID: {projectData.projectId})</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                isSuppleMode 
                  ? 'hover:supple-card-glass-hover supple-text-muted' 
                  : 'hover:bg-gray-100 text-gray-500'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className={`p-6 border-b ${
          isSuppleMode ? 'supple-border-subtle' : 'border-gray-200'
        }`}>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className={`text-center p-3 rounded-lg ${
              isSuppleMode ? 'supple-card-glass' : 'bg-gray-50'
            }`}>
              <div className={`text-2xl font-bold ${
                isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
              }`}>{summary.totalKeywords}</div>
              <div className={`text-xs ${
                isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
              }`}>Total Keywords</div>
            </div>
            <div className={`text-center p-3 rounded-lg ${
              isSuppleMode ? 'supple-card-glass' : 'bg-green-50'
            }`}>
              <div className="text-2xl font-bold text-green-600">{summary.page1}</div>
              <div className={`text-xs ${
                isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
              }`}>Page 1 (1-10)</div>
            </div>
            <div className={`text-center p-3 rounded-lg ${
              isSuppleMode ? 'supple-card-glass' : 'bg-yellow-50'
            }`}>
              <div className="text-2xl font-bold text-yellow-600">{summary.page2}</div>
              <div className={`text-xs ${
                isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
              }`}>Page 2 (11-20)</div>
            </div>
            <div className={`text-center p-3 rounded-lg ${
              isSuppleMode ? 'supple-card-glass' : 'bg-orange-50'
            }`}>
              <div className="text-2xl font-bold text-orange-600">{summary.page3}</div>
              <div className={`text-xs ${
                isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
              }`}>Page 3 (21-30)</div>
            </div>
            <div className={`text-center p-3 rounded-lg ${
              isSuppleMode ? 'supple-card-glass' : 'bg-red-50'
            }`}>
              <div className="text-2xl font-bold text-red-600">{summary.notFound}</div>
              <div className={`text-xs ${
                isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
              }`}>Not Found</div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto p-6 max-h-96">
          <div className="overflow-x-auto overflow-y-auto max-h-80">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${
                  isSuppleMode ? 'supple-border-subtle' : 'border-gray-200'
                }`}>
                  <th className={`text-left py-3 px-4 font-medium ${
                    isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                  }`}>Keyword</th>
                  <th className={`text-center py-3 px-4 font-medium ${
                    isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                  }`}>Position</th>
                  <th className={`text-center py-3 px-4 font-medium ${
                    isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                  }`}>Change</th>
                  <th className={`text-center py-3 px-4 font-medium ${
                    isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                  }`}>Volume</th>
                  <th className={`text-left py-3 px-4 font-medium ${
                    isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                  }`}>Last Check</th>
                </tr>
              </thead>
              <tbody>
                {positionData.map((keyword, index) => {
                  const latestPosition = keyword.positions[keyword.positions.length - 1];
                  const change = getPositionChange(keyword.positions);
                  
                  return (
                    <tr key={keyword.id} className={`border-b ${
                      isSuppleMode ? 'supple-border-subtle' : 'border-gray-100'
                    }`}>
                      <td className={`py-3 px-4 ${
                        isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                      }`}>
                        {keyword.name}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded text-sm font-medium ${
                          latestPosition.pos === 0 ? 'bg-red-100 text-red-800' :
                          latestPosition.pos <= 10 ? 'bg-green-100 text-green-800' :
                          latestPosition.pos <= 20 ? 'bg-yellow-100 text-yellow-800' :
                          latestPosition.pos <= 30 ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {latestPosition.pos === 0 ? 'Not Found' : latestPosition.pos}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center">
                          {getChangeIcon(change)}
                          {change !== null && change !== 0 && (
                            <span className={`ml-1 text-sm ${
                              change > 0 ? 'text-red-600' : 'text-green-600'
                            }`}>
                              {Math.abs(change)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={`py-3 px-4 text-center ${
                        isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
                      }`}>
                        {keyword.volume || '-'}
                      </td>
                      <td className={`py-3 px-4 ${
                        isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
                      }`}>
                        {latestPosition.date}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const RankBot = ({ command, formData, messageId, onComplete, onSendMessage, storedProjectData, storedPositionData, isStoredComplete }) => {
  const [progress, setProgress] = useState([]);
  const [projectData, setProjectData] = useState(storedProjectData || null);
  const [positionData, setPositionData] = useState(storedPositionData || null);
  const [showModal, setShowModal] = useState(false);
  const [isComplete, setIsComplete] = useState(isStoredComplete || false);
  const [showProgress, setShowProgress] = useState(!isStoredComplete);
  const [hasStarted, setHasStarted] = useState(isStoredComplete || false);
  const { isSuppleMode } = useAppearance();

  React.useEffect(() => {
    if (command === 'rank' && formData && !hasStarted && !isComplete) {
      setHasStarted(true);
      executeRankCommand();
    }
  }, [command, formData, hasStarted, isComplete]);

  // Handle stored data on component mount
  React.useEffect(() => {
    if (isStoredComplete && storedProjectData && storedPositionData) {
      setProgress([
        { status: 'success', message: 'Connecting to SEranking API...', timestamp: new Date() },
        { status: 'success', message: 'Fetching projects from SEranking...', timestamp: new Date() },
        { status: 'success', message: `Searching for domain: ${storedProjectData.domain}`, timestamp: new Date() },
        { status: 'success', message: `Found project: ${storedProjectData.projectName} (ID: ${storedProjectData.projectId})`, timestamp: new Date() },
        { status: 'success', message: 'Fetching ranking positions...', timestamp: new Date() },
        { status: 'success', message: `Retrieved ${storedPositionData.length} keywords with position data`, timestamp: new Date() }
      ]);
      setProjectData(storedProjectData);
      setPositionData(storedPositionData);
      setIsComplete(true);
    }
  }, [isStoredComplete, storedProjectData, storedPositionData]);

  const addProgress = (status, message) => {
    setProgress(prev => [...prev, { status, message, timestamp: new Date() }]);
  };

  const handleAnalyseRankings = () => {
    if (positionData && projectData) {
      const keywordData = positionData.map(keyword => {
        const latestPos = keyword.positions[keyword.positions.length - 1];
        const previousPos = keyword.positions.length > 1 ? keyword.positions[keyword.positions.length - 2] : null;
        const change = previousPos ? latestPos.pos - previousPos.pos : 0;
        
        return `• ${keyword.name}: Position ${latestPos.pos === 0 ? 'Not Found' : latestPos.pos}${change !== 0 ? ` (${change > 0 ? '+' : ''}${change})` : ''}`;
      }).join('\n');

      const analysisMessage = `Please analyse the complete SEO ranking data for ${projectData.projectName}:

Domain: ${projectData.domain}
Total Keywords: ${positionData.length}

Complete keyword ranking data:
${keywordData}

Please provide detailed insights on:
1. Overall ranking performance and trends
2. Keywords that need immediate attention (poor rankings)
3. Keywords performing well that can be leveraged
4. Recommended SEO strategies and action items
5. Competitive analysis based on ranking patterns`;

      onSendMessage && onSendMessage(analysisMessage);
    }
  };

  const executeRankCommand = async () => {
    try {
      addProgress('loading', 'Connecting to SEranking API...');
      
      await new Promise(resolve => setTimeout(resolve, 800));
      addProgress('loading', 'Fetching projects from SEranking...');
      
      await new Promise(resolve => setTimeout(resolve, 800));
      addProgress('loading', `Searching for domain: ${formData.domain}`);
      
      // Make actual API call
      const response = await callRankAPI(formData);
      
      if (response.success) {
        const projectInfo = {
          projectId: response.project.id,
          projectName: response.project.name,
          domain: response.project.domain
        };
        
        setProjectData(projectInfo);
        addProgress('success', `Found project: ${projectInfo.projectName} (ID: ${projectInfo.projectId})`);
        
        await new Promise(resolve => setTimeout(resolve, 500));
        addProgress('loading', 'Fetching ranking positions...');
        
        await new Promise(resolve => setTimeout(resolve, 800));
        
        setPositionData(response.keywords);
        addProgress('success', `Retrieved ${response.keywords.length} keywords with position data`);
        setIsComplete(true);
        
        // Call onComplete with the rank data
        if (onComplete) {
          onComplete({
            projectData: projectInfo,
            positionData: response.keywords
          });
        }
      } else {
        addProgress('error', 'Failed to retrieve ranking data');
      }
      
    } catch (error) {
      addProgress('error', `Error: ${error.message}`);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          isSuppleMode 
            ? 'supple-btn-gradient-blue' 
            : 'bg-blue-600'
        }`}>
          <TrendingUp className="w-4 h-4 text-white" />
        </div>
        <span className={`font-medium ${
          isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
        }`}>Rank Bot</span>
        
        {progress.length > 0 && (
          <button
            onClick={() => setShowProgress(!showProgress)}
            className={`ml-auto p-1 rounded transition-colors ${
              isSuppleMode 
                ? 'hover:supple-card-glass-hover supple-text-muted' 
                : 'hover:bg-gray-100 text-gray-500'
            }`}
            title={showProgress ? 'Hide progress' : 'Show progress'}
          >
            {showProgress ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        )}
      </div>

      {showProgress && progress.map((item, index) => (
        <RankBotProgress key={index} status={item.status} message={item.message} />
      ))}

      {isComplete && projectData && (
        <RankPreviewCard 
          projectData={projectData}
          onViewRankings={() => setShowModal(true)}
          onAnalyseRankings={handleAnalyseRankings}
        />
      )}

      {showModal && projectData && positionData && (
        <RankModal
          projectData={projectData}
          positionData={positionData}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default RankBot;